import { useEffect, useState } from "react";
import * as EventSourcePolyfill from "../helpers/eventsource-polyfill.js";

// Import children components to plug in and render.
import MessagingHeader from "./messagingHeader";
import MessagingBody from "./messagingBody";
import MessagingInputFooter from "./messagingInputFooter";

import { setJwt, setLastEventId, storeConversationId, getConversationId, getJwt, storeSentMessageContent, getSentMessageContent, getAuthToken, storeAuthToken, clearSentMessage, clearInMemoryData, setDeploymentConfiguration } from "../services/dataProvider";
import { subscribeToEventSource, closeEventSource } from '../services/eventSourceService';
import { sendTypingIndicator, sendTextMessage, getContinuityJwt, listConversations, listConversationEntries, closeConversation, getUnauthenticatedAccessToken, createConversation } from "../services/messagingService";
import * as ConversationEntryUtil from "../helpers/conversationEntryUtil";
import { CONVERSATION_CONSTANTS, STORAGE_KEYS, CLIENT_CONSTANTS } from "../helpers/constants";
import { setItemInWebStorage, clearWebStorage } from "../helpers/webstorageUtils";
import { util } from "../helpers/common";
import { prechatUtil } from "../helpers/prechatUtil.js";
import Prechat from "./prechat.js";
import CountdownTimer from "../helpers/countdownTimer.js";
import { clear } from "@testing-library/user-event/dist/clear.js";

export default function Conversation(props) {
    // Initialize a list of conversation entries.
    let [conversationEntries, setConversationEntries] = useState([]);
    // Initialize the conversation status.
    let [conversationStatus, setConversationStatus] = useState(CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION);
    // Tracks whether Pre-Chat form should be shown.
    let [showPrechatForm, setShowPrechatForm] = useState(false);
    // Tracks the most recent conversation message that was failed to send.
    let [failedMessage, setFailedMessage] = useState(undefined);


    // Initialize current participants that are actively typing (except end user).
    // Each key is the participant's `senderName` or `role` and each value is a reference to a CountdownTimer object.
    let [currentTypingParticipants, setCurrentTypingParticipants] = useState({});
    // Initialize whether at least 1 participant (not including end user) is typing.
    let [isAnotherParticipantTyping, setIsAnotherParticipantTyping] = useState(false);
    let [isEndUserMessage, setIsEndUserMessage] = useState(false);
    const [reloadTimerId, setReloadTimerId] = useState(null);


    useEffect(() => {
        // We define an async function inside the effect so we can use await
        const initializeConversation = async () => {
            try {
                // This 'try' block will now catch any errors from the functions below
                if (props.isExistingConversation) {
                    await handleExistingConversation();
                } else {
                    await handleNewConversation();
                }

                // This code only runs if the above steps succeed
                await handleSubscribeToEventSource();
                props.uiReady(true);

            } catch (error) {
                // If any of the awaited promises reject, the error is caught here
                console.error("Failed to initialize conversation:", error);
                props.showMessagingWindow(false);
            }
        };

        // Call the async function to start the process
        initializeConversation();

        // The cleanup function
        return () => {
            closeEventSource();
            cleanupMessagingData();
        };

        // let conversationStatePromise;
        // storeAuthToken(props.authToken);

        // conversationStatePromise = props.isExistingConversation ? handleExistingConversation() : handleNewConversation();
        // conversationStatePromise
        // .then(() => {
        //     handleSubscribeToEventSource()
        //     .then(props.uiReady(true)) // Let parent (i.e. MessagingWindow) know the app is UI ready so that the parent can decide to show the actual Messaging window UI.
        //     .catch(() => {
        //         props.showMessagingWindow(false);
        //     })
        // });

        // return () => {
        //     closeEventSource();
        //     cleanupMessagingData();
        //     // conversationStatePromise
        //     // .then(() => {
        //     //     cleanupMessagingData();
        // });
        // };
    }, []);

    useEffect(() => {
        // Now, the timer will reset for any message
        if (conversationEntries.length === 0) {
            return;
        }

        // Clear the previous timer
        if (reloadTimerId) {
            clearTimeout(reloadTimerId);
        }

        console.log('New message received. Resetting reload timer...');

        // Start a new timer
        const newTimerId = setTimeout(() => {
            console.log('Timer finished, reloading page.');
            window.location.reload();
        }, 300000);

        setReloadTimerId(newTimerId);
    }, [conversationEntries]);

    /**
     * Update conversation status state based on the event from a child component i.e. MessagingHeader.
     * Updating conversation status state re-renders the current component as well as the child components and the child components can reactively use the updated conversation status to make any changes.
     *
     * @param {string} status - e.g. CLOSED.
     */
    function updateConversationStatus(status) {
        setConversationStatus(status);
    }

    /**
     * Handles a new conversation.
     *
     * 1. Fetch an Unauthenticated Access Token i.e. Messaging JWT.
     * 2. Create a new conversation.
     * @returns {Promise}
     */
    async function handleNewConversation() {
        try {
            await handleGetUnauthenticatedJwt();

            if (prechatUtil.shouldDisplayPrechatForm()) {
                console.log("Pre-Chat is enabled. Continuing to render a Pre-Chat form.");
                setShowPrechatForm(true);
                return;
            }
            else {
                await handleCreateNewConversation();
            }
        }
        catch (error) {
            console.error("Failed to get JWT or create a new conversation:", error);
        }
        // return handleGetUnauthenticatedJwt()
        //         .then(() => {
        //             if (prechatUtil.shouldDisplayPrechatForm()) {
        //                 console.log("Pre-Chat is enabled. Continuing to render a Pre-Chat form.");
        //                 setShowPrechatForm(true);
        //                 return;
        //             }
        //             console.log("Pre-Chat is not enabled. Continuing to create a new conversation.");
        //             return handleCreateNewConversation()
        //                     .then(() => {
        //                         console.log(`Completed initializing a new conversation with conversationId: ${getConversationId()}`);
        //                     })
        //                     .catch(err => {
        //                         console.error(`${err}`);
        //                     });
        //         })
        //         .catch(err => {
        //             console.error(`${err}`);
        //         });
    }

    /**
     * Handles an existing conversation.
     *
     * 1. Fetch a Continuation Access Token i.e. Messaging JWT.
     * 2. Lists the available conversations and loads the current (also most-recent) conversation that is OPEN.
     * 3. Fetch the entries for the current conversation.
     * @returns {Promise}
     */
    async function handleExistingConversation() {
        try {
            await handleGetContinuityJwt();
            await handleListConversations();
            await handleListConversationEntries();

        } catch (error) {
            console.error(`${error}`);
        }
        // return handleGetContinuityJwt()
        //         .then(() => {
        //             return handleListConversations()
        //                     .then(() => {
        //                         console.log(`Successfully listed the conversations.`);
        //                         handleListConversationEntries()
        //                         .then(console.log(`Successfully retrieved entries for the current conversation: ${getConversationId()}`))
        //                         .catch(err => {
        //                             console.error(`${err}`);
        //                         });
        //                     })
        //                     .catch(err => {
        //                         console.error(`${err}`);
        //                     });
        //         })
        //         .catch(err => {
        //             console.error(`${err}`);
        //         });
    }

    /**
     * Handles fetching an Unauthenticated Access Token i.e. Messaging JWT.
     *
     * 1. If a JWT already exists, simply return.
     * 2. Makes a request to Unauthenticated Access Token endpoint.
     * 3. Updates the web storage with the latest JWT.
     * 4. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    async function handleGetUnauthenticatedJwt() {
        if (getJwt()) {
            console.warn("Messaging access token (JWT) already exists in the web storage. Discontinuing to create a new Unauthenticated access token.");
            await handleExistingConversation();
            // return handleExistingConversation().then(Promise.reject());
        }
        return getUnauthenticatedAccessToken()
            .then((response) => {
                console.log("Successfully fetched an Unauthenticated access token.");
                // Parse the response object which includes access-token (JWT), configutation data.
                if (typeof response === "object") {
                    setJwt(response.accessToken);
                    setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
                    setLastEventId(response.lastEventId);
                    setDeploymentConfiguration(response.context && response.context.configuration && response.context.configuration.embeddedServiceConfig);
                }
            })
            .catch((err) => {
                console.error(`Something went wrong in fetching an Unauthenticated access token: ${err && err.message ? err.message : err}`);
                handleMessagingErrors(err);
                cleanupMessagingData();
                props.showMessagingWindow(false);
                throw new Error("Failed to fetch an Unauthenticated access token.");
            });
    }

    /**
     * Handles creating a new conversation.
     *
     * 1. If a conversation is already open, simply return.
     * 2. Generate a new unique conversation-id and initialize in-memory.
     * 3. Makes a request to Create Conversation endpoint.
     * 4. Updates the conversation status internally to OPENED, for the associated components to reactively update.
     * 5. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleCreateNewConversation(routingAttributes) {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            console.warn("Cannot create a new conversation while a conversation is currently open.");
            return Promise.reject();
        }

        // Initialize a new unique conversation-id in-memory.
        storeConversationId(util.generateUUID());
        return createConversation(getConversationId(), routingAttributes)
            .then(() => {
                console.log(`Successfully created a new conversation with conversation-id: ${getConversationId()}`);
                updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION);
                props.showMessagingWindow(true);
            })
            .catch((err) => {
                console.error(`Something went wrong in creating a new conversation with conversation-id: ${getConversationId()}: ${err && err.message ? err.message : err}`);
                handleMessagingErrors(err);
                cleanupMessagingData();
                props.showMessagingWindow(false);
                throw new Error("Failed to create a new conversation.");
            });
    }

    /**
     * Handles fetching a Continuation Access Token i.e. Messaging JWT.
     *
     * 1. Makes a request to Continuation Access Token endpoint.
     * 2. Updates the web storage with the latest JWT.
     * 3. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleGetContinuityJwt() {
        return getContinuityJwt()
            .then((response) => {
                setJwt(response.accessToken);
                setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
            })
            .catch((err) => {
                console.error(`Something went wrong in fetching a Continuation Access Token: ${err && err.message ? err.message : err}`);
                handleMessagingErrors(err);
                throw new Error("Failed to fetch a Continuation access token.");
            });
    }

    /**
     * Handles fetching a list of all conversations available. This returns only conversations which are OPEN, unless otherwise specified in the request.
     *
     * 1. Makes a request to List Conversations endpoint.
     * 2. If there are multiple OPEN conversations, loads the conversation with the most-recent start time.
     * 3. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleListConversations() {
        return listConversations()
            .then((response) => {
                if (response && response.openConversationsFound > 0 && response.conversations.length) {
                    const openConversations = response.conversations;
                    if (openConversations.length > 1) {
                        console.warn(`Expected the user to be participating in 1 open conversation but instead found ${openConversations.length}. Loading the conversation with latest startTimestamp.`);
                        openConversations.sort((conversationA, conversationB) => conversationB.startTimestamp - conversationA.startTimestamp);
                    }
                    // Update conversation-id with the one from service.
                    storeConversationId(openConversations[0].conversationId);
                    updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION);
                    props.showMessagingWindow(true);
                } else {
                    // No open conversations found.
                    cleanupMessagingData();
                    props.showMessagingWindow(false);
                }
            })
            .catch((err) => {
                console.error(`Something went wrong in fetching a list of conversations: ${err && err.message ? err.message : err}`);
                handleMessagingErrors(err);
                throw new Error("Failed to list the conversations.");
            });
    }

    const handleFetchToken = async (email) => {

        try {
            // --- SECURITY WARNING ---
            // This authentication logic should be on a backend server, not here.
            // Your client_secret and password are exposed to the public.

            const urlencoded = new URLSearchParams();
            urlencoded.append("grant_type", "password");
            urlencoded.append("client_id", process.env.REACT_APP_CLIENT_ID);
            urlencoded.append("client_secret", process.env.REACT_APP_CLIENT_SECRET);
            urlencoded.append("username", process.env.REACT_APP_USERNAME);
            urlencoded.append("password", process.env.REACT_APP_PASSWORD);

            const requestOptions = {
                method: "POST",
                // The header 'Content-Type' is automatically set by the browser
                // when using URLSearchParams in the body, so it's not needed here.
                body: urlencoded,
            };

            // 1. Await the fetch call and store the result in 'response'
            const response = await fetch(process.env.REACT_APP_TOKEN_URL, requestOptions);
            // 2. Check if the server responded with an error
            if (!response.ok) {
                const errorData = response.json();
                console.error('Salesforce auth error:', errorData);
                throw new Error('Login failed. Please check your credentials.');
            }

            // 3. Get the JSON data from the successful response
            const data = await response.json();
            storeAuthToken(data.access_token);

            // 4. Now that authentication is successful, proceed to the chat page
            // props.onPrechatSubmit({ [fieldName]: email, 'authToken' : data.access_token });

        } catch (error) {
            console.error('Login process error:', error);
        }
    }

    /**
     * Handles fetching a list of all conversation entries for the current conversation.
     *
     * 1. Makes a request to List Conversation Entries endpoint.
     * 2. Renders the conversation entries based on their Entry Type.
     * @returns {Promise}
     */
    function handleListConversationEntries() {
        return listConversationEntries(getConversationId())
            .then((response) => {
                if (Array.isArray(response)) {
                    response.reverse().forEach(entry => {
                        const conversationEntry = generateConversationEntryForCurrentConversation(entry);
                        if (!conversationEntry) {
                            return;
                        }

                        switch (conversationEntry.entryType) {
                            case CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE:
                                conversationEntry.isEndUserMessage = ConversationEntryUtil.isMessageFromEndUser(conversationEntry);
                                addConversationEntry(conversationEntry);
                                break;
                            case CONVERSATION_CONSTANTS.EntryTypes.PARTICIPANT_CHANGED:
                            case CONVERSATION_CONSTANTS.EntryTypes.ROUTING_RESULT:
                                addConversationEntry(conversationEntry);
                                break;
                            default:
                                console.log(`Unrecognized conversation entry type: ${conversationEntry.entryType}.`);
                        }
                    });
                } else {
                    console.error(`Expecting a response of type Array from listConversationEntries but instead received: ${response}`);
                }
            })
            .catch((err) => {
                console.error(`Something went wrong while processing entries from listConversationEntries response:  ${err && err.message ? err.message : err}`);
                handleMessagingErrors(err);
                throw new Error("Failed to list the conversation entries for the current conversation.");
            }).finally(() => {
            });
    }

    /**
     * Handles establishing a connection to the EventSource i.e. SSE.
     * Selectively listens to the supported events in the app by adding the corresponding event listeners.
     * Note: Update the list of events/event-listeners to add/remove support for the available events. Refer https://developer.salesforce.com/docs/service/messaging-api/references/about/server-sent-events-structure.html
     * @returns {Promise}
     */
    function handleSubscribeToEventSource() {
        return subscribeToEventSource({
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_MESSAGE]: handleConversationMessageServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_ROUTING_RESULT]: handleRoutingResultServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_PARTICIPANT_CHANGED]: handleParticipantChangedServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STARTED_INDICATOR]: handleTypingStartedIndicatorServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STOPPED_INDICATOR]: handleTypingStoppedIndicatorServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_DELIVERY_ACKNOWLEDGEMENT]: handleConversationDeliveryAcknowledgementServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_READ_ACKNOWLEDGEMENT]: handleConversationReadAcknowledgementServerSentEvent,
            [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_CLOSE_CONVERSATION]: handleCloseConversationServerSentEvent
        })
            .then(() => {
                console.log("Subscribed to the Event Source (SSE).");
            })
            .catch((err) => {
                handleMessagingErrors(err);
                throw new Error(err);
            });
    }

    /**
     * Generate a Conversation Entry object from the server sent event.
     *
     * 1. Create a Conversation Entry object from the parsed event data.
     * 2. Return the Conversation Entry if the conversationEntry is for the current conversation and undefined, otherwise.
     * @param {object} event - Event data payload from server-sent event.
     * @returns {object|undefined}
     */
    function generateConversationEntryForCurrentConversation(parsedEventData) {
        const conversationEntry = ConversationEntryUtil.createConversationEntry(parsedEventData);

        // Handle server sent events only for the current conversation
        if (parsedEventData.conversationId === getConversationId()) {
            return conversationEntry;
        }
        console.log(`Current conversation-id: ${getConversationId()} does not match the conversation-id in server sent event: ${parsedEventData.conversationId}. Ignoring the event.`);
        return undefined;
    }

    /**
     * Adds a Conversation Entry object to the list of conversation entries. Updates the state of the list of conversation entries for the component(s) to be updated in-turn, reactively.
     * @param {object} conversationEntry - entry object for the current conversation.
     */
    function addConversationEntry(conversationEntry) {
        conversationEntries.push(conversationEntry);
        setConversationEntries([...conversationEntries]);
    }
    const validateEmailSuffix = (email) => {
        const legalSuffixes = ['salesforce.com', 'sohobb.jp'];
        const suffix = String(email).split('@')[1];
        return legalSuffixes.includes(suffix);
    }

    /**
     * Handle a CONVERSATION_MESSAGE server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type
     *      NOTE: Skip processing CONVERSATION_MESSAGE if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     * @param {object} event - Event data payload from server-sent event.
     */
    async function handleConversationMessageServerSentEvent(event) {
        try {
            if (validateEmailSuffix(props.userEmail) === false) {
                console.log("Invalid email suffix detected in conversation message event handler.");
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
                return;
            }
            console.log(`Successfully handling conversation message server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }
            setIsAnotherParticipantTyping(false);
            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            if (!conversationEntry) {
                return;
            }

            // console.log("logging props.authToken" + props.authToken);
            // console.log("logging props.userEmail" + props.userEmail);
            const messageType = conversationEntry.actorType;

            let sss = getSentMessageContent();
            console.log("logging sentMessage" + sss);

            if (messageType === "EndUser") {
                // setSentMessage(conversationEntry.content.staticContent.text);
                storeSentMessageContent(conversationEntry.content.staticContent.text);
            }
            else if (messageType === "Chatbot" && sss !== undefined) {

                let token = getAuthToken();
                console.log("logging to salesforce");
                const myHeaders = new Headers();
                myHeaders.append("Content-Type", "application/json");
                myHeaders.append("Authorization", "Bearer " + token);
                // myHeaders.append("Cookie", "BrowserId=6_GDlpLYEfCwEbfyLepL1A; CookieConsentPolicy=0:1; LSKey-c$CookieConsentPolicy=0:1");

                const raw = JSON.stringify({
                    "conversationID__c": conversationEntry.conversationId,
                    "userEmail__c": props.userEmail,
                    "userQuery__c": sss,
                    "agentAnswer__c": conversationEntry.content.staticContent.text,
                    "timeStamp__c": new Date().toISOString()
                });

                const requestOptions = {
                    method: "POST",
                    headers: myHeaders,
                    body: raw,
                    redirect: "follow"
                };

                try {
                    const response = await fetch(process.env.REACT_APP_RECORD_URL, requestOptions);
                    const result = await response.text();
                    console.log(result);
                    const msg = JSON.parse(result)
                    if (msg[0].message === "Session expired or invalid" || msg[0].message === "INVALID_AUTH_HEADER") {
                        console.log("handle expired token fail")
                        await handleFetchToken(props.userEmail);
                        token = getAuthToken();
                        console.log('token refreshed');
                        try {
                            const myHeaders = new Headers();
                            myHeaders.append("Content-Type", "application/json");
                            myHeaders.append("Authorization", "Bearer " + token);
                            // myHeaders.append("Cookie", "BrowserId=6_GDlpLYEfCwEbfyLepL1A; CookieConsentPolicy=0:1; LSKey-c$CookieConsentPolicy=0:1");
                            const requestOptions = {
                                method: "POST",
                                headers: myHeaders,
                                body: raw,
                                redirect: "follow"
                            };
                            const response = await fetch(process.env.REACT_APP_RECORD_URL, requestOptions);
                            const result = await response.text();
                            console.log(result)
                        } catch (error) {
                            console.log('token refresh failed');
                            console.error(error);
                            clearSentMessage();
                        }
                    }
                } catch (error) {
                    console.error(error);
                    clearSentMessage();
                }
                ;
            }

            if (ConversationEntryUtil.isMessageFromEndUser(conversationEntry)) {
                conversationEntry.isEndUserMessage = true;
                // Since message is echoed back by the server, mark the conversation entry as sent.
                conversationEntry.isSent = true;
                console.log(`End user successfully sent a message.`);
            } else {
                conversationEntry.isEndUserMessage = false;
                console.log(`Successfully received a message from ${conversationEntry.actorType}`);
            }

            addConversationEntry(conversationEntry);
            setIsEndUserMessage(true);
        } catch (err) {
            console.error(`Something went wrong in handling conversation message server sent event: ${err}`);
        }
    }

    /**
     * Handle a ROUTING_RESULT server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type.
     *      NOTE: Skip processing ROUTING_RESULT if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     *
     *  NOTE: Update the chat client based on the latest routing result. E.g. if the routing type is transfer, set an internal flag like `isTransferring` to 'true' and use that to show a transferring indicator in the ui.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleRoutingResultServerSentEvent(event) {
        try {
            console.log(`Successfully handling routing result server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            if (!conversationEntry) {
                return;
            }

            if (conversationEntry.messageType === CONVERSATION_CONSTANTS.RoutingTypes.INITIAL) {
                // Render reasonForNotRouting when initial routing fails.
                switch (conversationEntry.content.failureType) {
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.NO_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.SUBMISSION_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.ROUTING_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.UNKNOWN_ERROR:
                        addConversationEntry(conversationEntry);
                        break;
                    default:
                        console.error(`Unrecognized initial routing failure type: ${conversationEntry.content.failureType}`);
                }
                // Handle when a conversation is being transferred.
            } else if (conversationEntry.messageType === CONVERSATION_CONSTANTS.RoutingTypes.TRANSFER) {
                switch (conversationEntry.content.failureType) {
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.NO_ERROR:
                        // Render transfer timestamp when transfer is requested successfully.
                        // TODO: Add a transfer state ui update.
                        addConversationEntry(conversationEntry);
                        break;
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.SUBMISSION_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.ROUTING_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.UNKNOWN_ERROR:
                        break;
                    default:
                        console.error(`Unrecognized transfer routing failure type: ${conversationEntry.content.failureType}`);
                }
            } else {
                console.error(`Unrecognized routing type: ${conversationEntry.messageType}`);
            }
        } catch (err) {
            console.error(`Something went wrong in handling routing result server sent event: ${err}`);
        }
    }

    /**
     * Handle a PARTICIPANT_CHANGED server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type.
     *      NOTE: Skip processing PARTICIPANT_CHANGED if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleParticipantChangedServerSentEvent(event) {
        try {
            console.log(`Successfully handling participant changed server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            if (!conversationEntry) {
                return;
            }
            addConversationEntry(conversationEntry);
        } catch (err) {
            console.error(`Something went wrong in handling participant changed server sent event: ${err}`);
        }
    }

    /**
     * Handle a TYPING_STARTED_INDICATOR server-sent event.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleTypingStartedIndicatorServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle typing indicators only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                const senderName = ConversationEntryUtil.getSenderDisplayName(parsedEventData) || ConversationEntryUtil.getSenderRole(parsedEventData);
                const typingParticipantTimer = currentTypingParticipants[senderName] && currentTypingParticipants[senderName].countdownTimer;

                // If we have received typing indicator from this sender within the past 5 seconds, reset the timer
                // Otherwise, start a new timer
                if (ConversationEntryUtil.getSenderRole(parsedEventData) !== CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER) {
                    console.log(`Successfully handling typing started indicator server sent event.`);

                    if (typingParticipantTimer) {
                        typingParticipantTimer.reset(Date.now());
                    } else {
                        currentTypingParticipants[senderName] = {
                            countdownTimer: new CountdownTimer(() => {
                                delete currentTypingParticipants[senderName];

                                if (!Object.keys(currentTypingParticipants).length) {
                                    // setIsAnotherParticipantTyping(false);
                                }
                            }, CLIENT_CONSTANTS.TYPING_INDICATOR_DISPLAY_TIMEOUT, Date.now()),
                            role: parsedEventData.conversationEntry.sender.role
                        };

                        currentTypingParticipants[senderName].countdownTimer.start();
                    }

                    setIsAnotherParticipantTyping(true);
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling typing started indicator server sent event: ${err}`);
        }
    }

    /**
     * Handle a TYPING_STOPPED_INDICATOR server-sent event.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleTypingStoppedIndicatorServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle typing indicators only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                const senderName = ConversationEntryUtil.getSenderDisplayName(parsedEventData) || ConversationEntryUtil.getSenderRole(parsedEventData);

                delete currentTypingParticipants[senderName];

                if (!Object.keys(currentTypingParticipants).length) {
                    setIsAnotherParticipantTyping(false);
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling typing stopped indicator server sent event: ${err}`);
        }
    }

    /**
     * Handle a server-sent event CONVERSATION_DELIVERY_ACKNOWLEDGEMENT:
     * - Parse server-sent event and update a conversation entry (if it exists) with data from the event.
     * - Store delivery acknowledgement timestamp on conversation entry to show receipt timestamp.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleConversationDeliveryAcknowledgementServerSentEvent(event) {
        try {
            console.log(`Successfully handling conversation delivery acknowledgement server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle delivery acknowledgements only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                if (parsedEventData.conversationEntry && parsedEventData.conversationEntry.relatedRecords.length && parsedEventData.conversationEntry.entryPayload.length) {
                    // Store acknowledgement timestamp and identifier of message that was delivered.
                    const deliveredMessageId = parsedEventData.conversationEntry.relatedRecords[0];
                    const deliveryAcknowledgementTimestamp = JSON.parse(parsedEventData.conversationEntry.entryPayload).acknowledgementTimestamp;

                    // Make deep copy of conversation entries to find matching messageId. 
                    util.createDeepCopy(conversationEntries).filter((conversationEntry) => {
                        if (conversationEntry.messageId === deliveredMessageId) {
                            conversationEntry.isDelivered = true;
                            conversationEntry.deliveryAcknowledgementTimestamp = deliveryAcknowledgementTimestamp;
                        }
                    });
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling conversation delivery acknowledgement server sent event: ${err}`);
        }
    }

    /**
     * Handle a server-sent event CONVERSATION_READ_ACKNOWLEDGEMENT:
     * - Parse server-sent event and update a conversation entry (if it exists) with data from the event.
     * - Store read acknowledgement timestamp on conversation entry to show receipt timestamp.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleConversationReadAcknowledgementServerSentEvent(event) {
        try {
            console.log(`Successfully handling conversation read acknowledgement server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle read acknowledgements only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                if (parsedEventData.conversationEntry && parsedEventData.conversationEntry.relatedRecords.length && parsedEventData.conversationEntry.entryPayload.length) {
                    // Store acknowledgement timestamp and identifier of message that was read.
                    const readMessageId = parsedEventData.conversationEntry.relatedRecords[0];
                    const readAcknowledgementTimestamp = JSON.parse(parsedEventData.conversationEntry.entryPayload).acknowledgementTimestamp;

                    // Make deep copy of conversation entries to find matching messageId. 
                    util.createDeepCopy(conversationEntries).filter((conversationEntry) => {
                        if (conversationEntry.messageId === readMessageId) {
                            conversationEntry.isRead = true;
                            conversationEntry.readAcknowledgementTimestamp = readAcknowledgementTimestamp;
                        }
                    });
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling conversation read acknowledgement server sent event: ${err}`);
        }
    }

    /**
     * Handle a CONVERSATION_CLOSED server-sent event.
     *
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleCloseConversationServerSentEvent(event) {
        try {
            console.log(`Successfully handling close conversation server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);

            // Do not render conversation ended text if the conversation entry is not for the current conversation.
            if (getConversationId() === parsedEventData.conversationId) {
                // Update state to conversation closed status.
                updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION);
            }
        } catch (err) {
            console.error(`Something went wrong while handling conversation closed server sent event in conversation ${getConversationId()}: ${err}`);
        }
    }

    /**
     * Handle sending a static text message in a conversation.
     * @param {string} conversationId - Identifier of the conversation.
     * @param {string} value - Actual text of the message.
     * @param {string} messageId - Unique identifier of the message.
     * @param {string} inReplyToMessageId - Identifier of another message where this message is being replied to.
     * @param {boolean} isNewMessagingSession - Whether it is a new messaging session.
     * @param {object} routingAttributes - Pre-Chat fields and its values, if configured.
     * @param {object} language - language code.
     * 
     * @returns {Promise}
     */
    function handleSendTextMessage(conversationId, value, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language) {
        return sendTextMessage(conversationId, value, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language)
            .catch((err) => {
                console.error(`Something went wrong while sending a message to conversation ${conversationId}: ${err}`);
                setFailedMessage(Object.assign({}, { messageId, value, inReplyToMessageId, isNewMessagingSession, routingAttributes, language }));
                handleMessagingErrors(err);
            });
    }

    /**
     * Close messaging window handler for the event from a child component i.e. MessagingHeader.
     * When such event is received, invoke the parent's handler to close the messaging window if the conversation status is closed or not yet started.
     */
    function endConversation() {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            // End the conversation if it is currently opened.
            return closeConversation(getConversationId())
                .then(() => {
                    console.log(`Successfully closed the conversation with conversation-id: ${getConversationId()}`);
                })
                .catch((err) => {
                    console.error(`Something went wrong in closing the conversation with conversation-id ${getConversationId()}: ${err}`);
                })
                .finally(() => {
                    cleanupMessagingData();
                });
        }
    }

    /**
     * Close messaging window handler for the event from a child component i.e. MessagingHeader.
     * When such event is received, invoke the parent's handler to close the messaging window if the conversation status is closed or not yet started.
     */
    function closeMessagingWindow() {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION || conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION) {
            props.showMessagingWindow(false);
        }
    }

    /**
     * Performs a cleanup in the app.
     * 1. Closes the EventSource connection.
     * 2. Clears the web storage.
     * 3. Clears the in-memory messaging data.
     * 4. Update the internal conversation status to CLOSED.
     */
    function cleanupMessagingData() {
        closeEventSource()
            .then(console.log("Closed the Event Source (SSE)."))
            .catch((err) => {
                console.error(`Something went wrong in closing the Event Source (SSE): ${err}`);
            });

        clearWebStorage();
        clearInMemoryData();

        // Update state to conversation closed status.
        updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION);
    }

    /**
     * Handles the errors from messaging endpoint requests.
     * If a request is failed due to an Unauthorized error (i.e. 401), peforms a cleanup and resets the app and console logs otherwise.
     */
    function handleMessagingErrors(err) {
        if (typeof err === "object") {
            if (err.status) {
                switch (err.status) {
                    case 401:
                        console.error(`Unauthenticated request: ${err.message}`);
                        cleanupMessagingData();
                        props.showMessagingWindow(false);
                        break;
                    case 400:
                        console.error(`Invalid request parameters. Please check your data before retrying: ${err.message}`);
                        break;
                    case 404:
                        console.error(`Resource not found. Please check your data before retrying: ${err.message}`);
                        break;
                    case 429:
                        console.warn(`Too many requests issued from the app. Try again in sometime: ${err.message}`);
                        break;
                    /**
                     * HTTP error code returned by the API(s) when a message is sent and failed because no messaging session exists. This error indicates client
                     * to surface the Pre-Chat form if configured to show for every new messaging session and then retry the failed message with routingAttributes.
                     */
                    case 417:
                        if (prechatUtil.shouldDisplayPrechatForm() && prechatUtil.shouldDisplayPrechatEveryMessagingSession()) {
                            console.log("Pre-Chat configured to show for every new messaging session. Continuing to display the Pre-Chat form.");
                            setShowPrechatForm(true);
                        }
                    case 500:
                        console.error(`Something went wrong in the request, please try again: ${err.message}`);
                        break;
                    default:
                        console.error(`Unhandled/Unknown http error: ${err}`);
                        cleanupMessagingData();
                        props.showMessagingWindow(false);
                }
                return;
            }
            console.error(`Something went wrong: ${err && err.message ? err.message : err}`);
        }
        return;
    }

    /**
     * Handles submitting a Pre-Chat form to either start a new conversation or a new messaging session.
     * @param {object} prechatData - Object containing key-value pairs of Pre-Chat form fields and their corresponding values.
     */
    function handlePrechatSubmit(prechatData) {
        let prechatSubmitPromise;
        console.log(`Pre-Chat fields values on submit: ${prechatData}.`);

        // If there is a failed message being tracked while submitting Pre-Chat form, consider it is an existing conversation but a new messaging session. Resend the failed message with the routing attributes to begin a new messaging session.
        if (failedMessage) {
            prechatSubmitPromise = handleSendTextMessage(getConversationId(), failedMessage.value, failedMessage.messageId, failedMessage.inReplyToMessageId, true, prechatData, failedMessage.language);
        } else {
            // If there is no failed message being tracked while submitting Pre-Chat form, create a new conversation.
            prechatSubmitPromise = handleCreateNewConversation(prechatData);
        }
        prechatSubmitPromise
            .then(() => {
                setShowPrechatForm(false);
            });
    }

    return (
        <>
            <MessagingHeader
                conversationStatus={conversationStatus}
                endConversation={endConversation}
                closeMessagingWindow={closeMessagingWindow} />
            {!showPrechatForm &&
                <>
                    <MessagingBody
                        conversationEntries={conversationEntries}
                        conversationStatus={conversationStatus}
                        typingParticipants={currentTypingParticipants}
                        showTypingIndicator={isAnotherParticipantTyping} />
                    <MessagingInputFooter
                        conversationStatus={conversationStatus}
                        sendTextMessage={handleSendTextMessage}
                        defValue={props.defValue}
                        isEndUserMessage={isEndUserMessage}
                        sendTypingIndicator={sendTypingIndicator} />
                </>
            }
            {
                showPrechatForm &&
                <Prechat prechatSubmit={handlePrechatSubmit} />
            }
        </>
    );
}